<template>
  <QPageContainer>
    <QPage class="recruitment-candidates-list">
      <transition appear :enter-active-class="transition.classes.enter" :leave-active-class="transition.classes.leave">
        <section>
          <h2 class="q-ma-md text-center text-subtitle1 font-poppins">
            {{ t("ETAB.PAGES.CANDIDATE.@EXPIRED_DEMAND_SUBTITLE", { recruitment: recruitment.getQualificationLabel() }) }}
          </h2>
          <transition-group tag="div" name="candidate-list">
            <div v-if="candidates && candidates.length > 0" key="contacted" class="candidates-list">
              <header>
                <h3>{{ tc("ETAB.PAGES.RECRUITMENT.@CONTACTED_CANDIDATES", candidates.length) }}</h3>
              </header>
              <QList>
                <PermanentCandidatesListItem
                  v-for="candidate in candidates"
                  :key="candidate.getId()"
                  :candidate="candidate"
                  :recruitment="recruitment"
                  :has-recruited="hasRecruited"
                  @on-recruited-candidate="onRecruitedCandidate"
                  @open-report-modal="openReportModal"
                  @call-candidate="callCandidate"
                  @email-candidate="emailCandidate"
                />
              </QList>
            </div>
          </transition-group>
          <NoCandidate v-if="candidates.length === 0" :img="placeholderImg" :title="$t('ETAB.PAGES.CANDIDATE.@NO_PERMANENT_CANDIDATES')" />

          <QCard class="expired-recruitment-feedback">
            <QCardSection>
              <h3 class="q-pt-sm text-center text-weight-bold">
                {{ $t("ETAB.PAGES.RECRUITMENT.FEEDBACK_FORM.@TITLE") }}
              </h3>

              <ExpiredRecruitmentFeedbackForm @save="saveFeedbackForm" />
            </QCardSection>
          </QCard>
        </section>
      </transition>
    </QPage>
  </QPageContainer>
</template>

<script setup>
  import {
    GETTER_RECRUITMENT_GET_CURRENT,
    GETTER_RECRUITMENT_CANDIDATES_GET_DEMAND_CONTACTED,
    ACTION_RECRUITMENT_CANDIDATES_GET_DEMAND_CONTACTED,
    ACTION_REPORT_MODAL_SET,
    ACTION_REPORT_CANDIDATE_SET,
    ACTION_REPORT_DEMAND_SET,
  } from "@etab/stores/types";
  import { ETAB_ROUTES } from "@etab/config";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { RecruitmentCandidatesService, SurveyService } from "@core/services";
  import { ScrollManager } from "@core/managers/scroll.manager";
  import { NativeCommunicationManager } from "@core/managers/native-communication.manager";
  import { useRecruitmentStore } from "@etab/stores/recruitment";
  import { useRecruitmentCandidatesStore } from "@etab/stores/recruitment-candidates";
  import { useReportCandidateStore } from "@etab/stores/report-candidate";
  import { useTransition } from "@core/composables";
  import { useI18n } from "vue-i18n";
  import { computed } from "vue";
  import { LoadingManager } from "@core/managers/loading.manager";
  import { useRoute, useRouter } from "vue-router";

  import NoCandidate from "@etab/components/candidates/NoCandidate.functional.vue";
  import PermanentCandidatesListItem from "@etab/components/candidates/PermanentCandidates.listItem.vue";
  import ExpiredRecruitmentFeedbackForm from "@etab/components/recruitments/detail/ExpiredRecruitmentFeedback.form.vue";

  import * as placeholderImg from "@core/assets/img/user_photo_placeholder.svg";

  const { transition } = useTransition();
  const route = useRoute();
  const router = useRouter();
  const { t } = useI18n();

  const recruitmentStore = useRecruitmentStore();
  const recruitmentCandidatesStore = useRecruitmentCandidatesStore();
  const reportCandidateStore = useReportCandidateStore();

  // Computed
  const demandId = computed(() => {
    return route.params.demandId;
  });

  const hasRecruited = computed(() => {
    return route.name === "recruitment-permanent-recruited";
  });

  const recruitment = computed(() => {
    return recruitmentStore[GETTER_RECRUITMENT_GET_CURRENT];
  });

  const candidates = computed(() => {
    return recruitmentCandidatesStore[GETTER_RECRUITMENT_CANDIDATES_GET_DEMAND_CONTACTED];
  });

  // Methods
  const callCandidate = function ({ candidate }) {
    const phone = candidate.getPhone();
    NativeCommunicationManager.phone({ phone });
  };

  const emailCandidate = function ({ candidate }) {
    const to = candidate.getEmail();
    NativeCommunicationManager.sendEmail({ to });
  };

  const openReportModal = function ({ candidate }) {
    reportCandidateStore[ACTION_REPORT_MODAL_SET](true);
    reportCandidateStore[ACTION_REPORT_CANDIDATE_SET]({ candidateId: candidate.getId(), candidateFirstname: candidate.getFirstname() });
    reportCandidateStore[ACTION_REPORT_DEMAND_SET](demandId.value);
  };

  const onRecruitedCandidate = async function ({ candidate, demand, recruited }) {
    try {
      LoadingManager.show();
      await RecruitmentCandidatesService.recruitedPermanentCandidate({
        demandId: demand.getDemandId(),
        candidateId: candidate.getId(),
        recruited,
      });
    } catch (error) {
      NotificationsManager.error();
      throw error;
    } finally {
      LoadingManager.hide();
      refreshCandidates();
    }
  };

  const saveFeedbackForm = async function ({ comment }) {
    try {
      LoadingManager.show();
      await SurveyService.postDemandReview({ demandId: demandId.value, comment });
      const message = t("ETAB.PAGES.RECRUITMENT.FEEDBACK_FORM.@CLOSE_DEMAND_SUCCESS");
      NotificationsManager.success({ message });
    } catch (error) {
      NotificationsManager.error();
      throw error;
    } finally {
      LoadingManager.hide();
      router.push({ name: ETAB_ROUTES.dashboard.tabs.upcoming.name }).catch((suppressedError) => {});
    }
  };

  const refreshCandidates = async function () {
    try {
      LoadingManager.show();
      await recruitmentCandidatesStore[ACTION_RECRUITMENT_CANDIDATES_GET_DEMAND_CONTACTED]({ demandId: demandId.value });
    } catch (error) {
      NotificationsManager.error();
      throw error;
    } finally {
      LoadingManager.hide();
    }
  };

  // Created
  ScrollManager.scrollToTop();
</script>

<style lang="scss">
  .candidates-list {
    header {
      h3 {
        margin: 0;
        font-weight: bold;
        line-height: 35px;
      }
    }
  }
  .expired-recruitment-feedback {
    box-shadow: $card-shadow;
  }
</style>
